import React, { useState, useEffect, useRef } from 'react';
import { Heart, Zap, Shield, ShoppingBag, Diamond } from 'lucide-react';

const ShopItem = ({ item, buyItem, gems, darkMode, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={ref}
      className="widget-card shop-item"
      style={{
        backgroundColor: darkMode ? 'rgba(43, 43, 43, 0.05)' : 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        border: darkMode ? '2px solid #444' : '2px solid #e5e5e5',
        borderRadius: '24px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        opacity: isVisible ? 1 : 0,
        animation: isVisible ? `pop-up 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${index * 0.1}s both` : 'none',
        transition: 'transform 0.2s',
        cursor: 'default',
        position: 'relative'
      }}
      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-5px)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
    >
      {(item.maxCount !== undefined || item.badgeText) && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: darkMode ? '#555' : '#e0e0e0',
          color: darkMode ? '#fff' : '#555',
          fontSize: '0.65rem',
          fontWeight: '800',
          padding: '2px 6px',
          borderRadius: '6px'
        }}>
          {item.badgeText ? item.badgeText : `${item.currentCount} / ${item.maxCount}`}
        </div>
      )}
      <div className="shop-item-icon" style={{ 
          marginBottom: '15px', 
          padding: '15px', 
          background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', 
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
      }}>
        {item.icon}
      </div>
      <div className="shop-item-info" style={{ width: '100%' }}>
        <h3 style={{ margin: '0 0 5px 0', color: darkMode ? '#fff' : '#3c3c3c', fontSize: '1.1rem', fontWeight: '800' }}>{item.name}</h3>
        <p style={{ margin: '0 0 15px 0', color: darkMode ? '#aaa' : '#777', fontSize: '0.85rem', lineHeight: '1.4', minHeight: '40px', fontWeight: '600' }}>{item.desc}</p>
        {(() => {
          const isMaxed = item.maxCount !== undefined && item.currentCount >= item.maxCount;
          const btnDisabled = isMaxed || item.disableBuy || (!item.isAd && gems < item.price);
          return (
            <button 
              className="shop-buy-button" 
              onClick={() => { if (navigator.vibrate) navigator.vibrate(10); buyItem(item.price, item.id); }}
              disabled={btnDisabled}
              style={{
                width: '100%',
                height: '45px',
                padding: '0 12px',
                border: 'none',
                borderRadius: '16px',
                background: item.isAd ? '#1cb0f6' : (btnDisabled ? (darkMode ? '#333' : '#e5e5e5') : '#58cc02'),
                color: (item.isAd || !btnDisabled) ? 'white' : (darkMode ? '#555' : '#afafaf'),
                fontWeight: '800',
                cursor: btnDisabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: btnDisabled ? 'none' : (item.isAd ? '0 4px 0 #148ac2' : '0 4px 0 #46a302'),
                transition: 'transform 0.1s',
                fontSize: '0.95rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseDown={e => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'translateY(4px)', e.currentTarget.style.boxShadow = 'none')}
              onMouseUp={e => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = (item.isAd ? '0 4px 0 #148ac2' : '0 4px 0 #46a302'))}
              onMouseLeave={e => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = (item.isAd ? '0 4px 0 #148ac2' : '0 4px 0 #46a302'))}
            >
              {isMaxed ? "MAX" : (item.isAd ? item.buttonText : <><Diamond size={20} color={!btnDisabled ? "#1cb0f6" : "currentColor"} style={{marginRight: '8px'}} /> {item.price}</>)}
            </button>
          );
        })()}
      </div>
    </div>
  );
};

function Shop({ gems, buyItem, darkMode, streakFreezes, xpBoostEndTime }) {
  const [xpBoostTimeLeft, setXpBoostTimeLeft] = useState('');

  useEffect(() => {
    if (xpBoostEndTime && xpBoostEndTime > Date.now()) {
      const updateTimer = () => {
        const diff = xpBoostEndTime - Date.now();
        if (diff > 0) {
          const m = Math.floor(diff / 60000);
          const s = Math.floor((diff % 60000) / 1000);
          setXpBoostTimeLeft(`${m}:${s < 10 ? '0' : ''}${s}`);
        } else {
          setXpBoostTimeLeft('');
        }
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setXpBoostTimeLeft('');
    }
  }, [xpBoostEndTime]);

  const heartItems = [
    { id: 'heart', name: 'Herzen auffüllen', price: 350, icon: <Heart size={32} color="#ff4b4b" fill="#ff4b4b" />, desc: 'Füllt alle deine Herzen wieder auf.' },
    { id: 'ad_heart', name: '+1 Herz', price: 0, icon: <Heart size={32} color="#ff4b4b" fill="#ff4b4b" />, desc: 'Werbung schauen und 1 Herz verdienen.', isAd: true, buttonText: 'WERBUNG' },
  ];

  const powerUpItems = [
    { id: 'freeze', name: 'Streak Freeze', price: 200, icon: <Shield size={32} color="#1cb0f6" />, desc: 'Schützt deinen Streak für einen Tag.', maxCount: 5, currentCount: streakFreezes },
    { id: 'xpboost', name: 'Doppel-XP', price: 150, icon: <Zap size={32} color="#ffc800" fill="#ffc800" />, desc: 'Verdiene doppelte XP für 15 Minuten.', badgeText: xpBoostTimeLeft, disableBuy: !!xpBoostTimeLeft },
  ];

  return (
    <main className="main-content" style={{ height: '100vh', overflowY: 'auto', paddingBottom: '40px' }}>
      <style>{`
        @keyframes pop-up {
          0% { transform: scale(0.95); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div className="main-content-header" style={{ position: 'sticky', top: '20px', zIndex: 10, backgroundColor: '#1cb0f6', borderBottom: '4px solid #148ac2', borderRadius: '16px', color: 'white', marginBottom: '20px', marginTop: '20px' }}>
        <h2>Shop</h2>
        <span>Gib deine Edelsteine aus!</span>
      </div>
      
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 20px' }}>
        <h3 style={{ fontSize: '1.2rem', marginBottom: '20px', color: darkMode ? '#fff' : '#3c3c3c', fontWeight: '800' }}>Herzen</h3>
        <div className="shop-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
          {heartItems.map((item, index) => (
            <ShopItem 
              key={item.id} 
              item={item} 
              buyItem={buyItem} 
              gems={gems} 
              darkMode={darkMode} 
              index={index} 
            />
          ))}
        </div>

        <div style={{ height: '2px', backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', margin: '30px 0' }}></div>

        <h3 style={{ fontSize: '1.2rem', marginBottom: '20px', color: darkMode ? '#fff' : '#3c3c3c', fontWeight: '800' }}>Power Ups</h3>
        <div className="shop-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {powerUpItems.map((item, index) => (
          <ShopItem 
            key={item.id} 
            item={item} 
            buyItem={buyItem} 
            gems={gems} 
            darkMode={darkMode} 
            index={index + heartItems.length} 
          />
        ))}
      </div>
      </div>
    </main>
  );
}

export default Shop;